import React, { useState, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import {
  Search, ArrowUpDown, Star, Zap, Clock, DollarSign, Brain, Layers,
  ChevronRight, TrendingUp, BarChart3, ExternalLink, Sparkles, X, Filter,
} from "lucide-react";

const MODELS = [
  { id: "gpt-5.2", provider: "OpenAI", quality: 97, inputCost: 5.00, outputCost: 15.00, latency: 520, context: 128000, tags: ["reasoning", "code", "multimodal"], featured: true, description: "Most advanced reasoning model with native tool use and multimodal capabilities", benchmarks: { mmlu: 92.1, humaneval: 94.8, math: 90.3 }, monthlyRequests: "8.4M", avgTokens: 1240 },
  { id: "gpt-4o", provider: "OpenAI", quality: 94, inputCost: 2.50, outputCost: 10.00, latency: 380, context: 128000, tags: ["general", "multimodal", "fast"], featured: true, description: "High-quality multimodal model optimized for speed and cost", benchmarks: { mmlu: 88.7, humaneval: 90.2, math: 85.4 }, monthlyRequests: "14.2M", avgTokens: 890 },
  { id: "gpt-4o-mini", provider: "OpenAI", quality: 82, inputCost: 0.15, outputCost: 0.60, latency: 180, context: 128000, tags: ["fast", "cost-effective"], featured: false, description: "Cost-effective model for lightweight tasks with excellent speed", benchmarks: { mmlu: 82.0, humaneval: 87.0, math: 74.0 }, monthlyRequests: "22.1M", avgTokens: 620 },
  { id: "claude-sonnet-4", provider: "Anthropic", quality: 95, inputCost: 3.00, outputCost: 15.00, latency: 450, context: 200000, tags: ["reasoning", "code", "long-context"], featured: true, description: "Anthropic's flagship model with extended thinking and 200K context", benchmarks: { mmlu: 90.4, humaneval: 93.1, math: 88.7 }, monthlyRequests: "6.1M", avgTokens: 1380 },
  { id: "claude-haiku-4", provider: "Anthropic", quality: 80, inputCost: 0.25, outputCost: 1.25, latency: 140, context: 200000, tags: ["fast", "cost-effective", "tool-use"], featured: false, description: "Fast and cost-effective with excellent tool use capabilities", benchmarks: { mmlu: 79.8, humaneval: 85.0, math: 72.0 }, monthlyRequests: "11.3M", avgTokens: 480 },
  { id: "gemini-3-flash", provider: "Google", quality: 88, inputCost: 0.10, outputCost: 0.40, latency: 120, context: 1000000, tags: ["fast", "multimodal", "1M-context"], featured: true, description: "Ultra-fast with 1M token context and native multimodal support", benchmarks: { mmlu: 85.2, humaneval: 88.0, math: 80.1 }, monthlyRequests: "18.7M", avgTokens: 710 },
  { id: "gemini-3-pro", provider: "Google", quality: 93, inputCost: 1.25, outputCost: 5.00, latency: 340, context: 1000000, tags: ["reasoning", "multimodal", "1M-context"], featured: false, description: "Google's most capable model with deep reasoning and million-token context", benchmarks: { mmlu: 89.6, humaneval: 91.5, math: 87.2 }, monthlyRequests: "5.8M", avgTokens: 1100 },
  { id: "kimi-k2.5", provider: "Moonshot", quality: 86, inputCost: 0.80, outputCost: 2.00, latency: 290, context: 256000, tags: ["reasoning", "multilingual", "256K-context"], featured: true, description: "Moonshot's flagship model with strong multilingual and reasoning capabilities", benchmarks: { mmlu: 84.3, humaneval: 87.8, math: 82.0 }, monthlyRequests: "2.1M", avgTokens: 950 },
  { id: "mistral-large", provider: "Mistral", quality: 89, inputCost: 2.00, outputCost: 6.00, latency: 320, context: 128000, tags: ["reasoning", "code", "EU-hosted"], featured: false, description: "Mistral's flagship model with excellent European data compliance", benchmarks: { mmlu: 86.0, humaneval: 89.4, math: 83.5 }, monthlyRequests: "7.3M", avgTokens: 1050 },
  { id: "llama-3.1-405b", provider: "Together", quality: 90, inputCost: 1.00, outputCost: 3.00, latency: 420, context: 128000, tags: ["open-source", "reasoning", "self-host"], featured: false, description: "Meta's largest open-source model, available for self-hosting", benchmarks: { mmlu: 87.5, humaneval: 89.0, math: 84.0 }, monthlyRequests: "3.9M", avgTokens: 1180 },
  { id: "deepseek-v3", provider: "DeepSeek", quality: 87, inputCost: 0.27, outputCost: 1.10, latency: 350, context: 128000, tags: ["code", "reasoning", "cost-effective"], featured: false, description: "Highly cost-effective model excelling at code generation and reasoning tasks", benchmarks: { mmlu: 84.8, humaneval: 90.5, math: 85.0 }, monthlyRequests: "1.8M", avgTokens: 890 },
  { id: "gemma-4-27b-it", provider: "Self-Hosted", quality: 78, inputCost: 0.00, outputCost: 0.00, latency: 12, context: 32000, tags: ["open-source", "self-host", "on-prem"], featured: true, description: "Google's open model optimized for on-premise deployment with zero API cost", benchmarks: { mmlu: 76.2, humaneval: 80.0, math: 68.0 }, monthlyRequests: "0.4M", avgTokens: 520 },
];

const PROVIDER_COLORS = {
  OpenAI: "#00A67E", Anthropic: "#E87B35", Google: "#4285F4", Moonshot: "#8B5CF6",
  Mistral: "#FF7000", Together: "#06B6D4", DeepSeek: "#3B82F6", "Self-Hosted": "#F59E0B",
};

function QualityBar({ value }) {
  return (
    <div className="flex items-center gap-2">
      <div className="w-16 h-1.5 rounded-full bg-[var(--qn-surface)] overflow-hidden">
        <div className="h-full rounded-full transition-all" style={{ width: `${value}%`, background: value >= 90 ? "#10b981" : value >= 80 ? "#f59e0b" : "#ef4444" }} />
      </div>
      <span className="text-xs font-mono text-[var(--qn-foreground)]">{value}</span>
    </div>
  );
}

export default function ModelCatalog() {
  const [search, setSearch] = useState("");
  const [sortBy, setSortBy] = useState("quality");
  const [filterProvider, setFilterProvider] = useState("all");
  const [filterTag, setFilterTag] = useState("all");
  const [drawerModel, setDrawerModel] = useState(null);

  const allTags = useMemo(() => [...new Set(MODELS.flatMap((m) => m.tags))].sort(), []);
  const allProviders = useMemo(() => [...new Set(MODELS.map((m) => m.provider))].sort(), []);
  const featured = useMemo(() => MODELS.filter((m) => m.featured), []);

  const filtered = useMemo(() => {
    let list = MODELS.filter((m) => {
      if (search && !m.id.toLowerCase().includes(search.toLowerCase()) && !m.provider.toLowerCase().includes(search.toLowerCase())) return false;
      if (filterProvider !== "all" && m.provider !== filterProvider) return false;
      if (filterTag !== "all" && !m.tags.includes(filterTag)) return false;
      return true;
    });
    list.sort((a, b) => {
      if (sortBy === "quality") return b.quality - a.quality;
      if (sortBy === "cost") return a.inputCost - b.inputCost;
      if (sortBy === "latency") return a.latency - b.latency;
      if (sortBy === "context") return b.context - a.context;
      if (sortBy === "name") return a.id.localeCompare(b.id);
      return 0;
    });
    return list;
  }, [search, sortBy, filterProvider, filterTag]);

  return (
    <div className="space-y-6" data-testid="model-catalog">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-outfit font-bold text-[var(--qn-foreground)]">Model Catalog</h1>
        <p className="text-sm text-[var(--qn-foreground-muted)] mt-1">Compare models across providers — pricing, benchmarks, and capabilities</p>
      </div>

      {/* Featured Strip */}
      <div data-testid="featured-models-strip">
        <div className="flex items-center gap-2 mb-3">
          <Sparkles className="w-4 h-4 text-amber-500" />
          <span className="text-sm font-semibold text-[var(--qn-foreground)]">Spotlight Models</span>
        </div>
        <div className="flex gap-3 overflow-x-auto pb-2 scrollbar-thin">
          {featured.map((m) => (
            <motion.button
              key={m.id}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={() => setDrawerModel(m)}
              className="flex-shrink-0 p-3 rounded-xl border border-amber-500/20 bg-gradient-to-br from-amber-500/5 to-transparent hover:border-amber-500/40 transition-all min-w-[200px]"
              data-testid={`featured-model-${m.id}`}
            >
              <div className="flex items-center gap-2 mb-1.5">
                <Star className="w-3.5 h-3.5 text-amber-500 fill-amber-500" />
                <span className="text-sm font-medium text-[var(--qn-foreground)]">{m.id}</span>
              </div>
              <div className="text-[10px] text-[var(--qn-foreground-muted)]">{m.provider}</div>
              <div className="flex items-center gap-3 mt-2 text-[10px]">
                <span className="text-emerald-500 font-mono">Q:{m.quality}</span>
                <span className="text-[var(--qn-foreground-muted)] font-mono">{m.latency}ms</span>
                <span className="text-amber-500 font-mono">${m.inputCost}/M</span>
              </div>
            </motion.button>
          ))}
        </div>
      </div>

      {/* Search & Filters */}
      <div className="flex items-center gap-3 flex-wrap">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[var(--qn-foreground-muted)]" />
          <Input
            placeholder="Search models..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-10 bg-[var(--qn-surface)] border-[var(--qn-border)]"
            data-testid="model-search"
          />
        </div>
        <Select value={filterProvider} onValueChange={setFilterProvider}>
          <SelectTrigger className="w-36 bg-[var(--qn-surface)] border-[var(--qn-border)]" data-testid="model-filter-provider">
            <Filter className="w-3.5 h-3.5 mr-1.5" /><SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Providers</SelectItem>
            {allProviders.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={filterTag} onValueChange={setFilterTag}>
          <SelectTrigger className="w-36 bg-[var(--qn-surface)] border-[var(--qn-border)]" data-testid="model-filter-tag">
            <Layers className="w-3.5 h-3.5 mr-1.5" /><SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Tags</SelectItem>
            {allTags.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={sortBy} onValueChange={setSortBy}>
          <SelectTrigger className="w-36 bg-[var(--qn-surface)] border-[var(--qn-border)]" data-testid="model-sort">
            <ArrowUpDown className="w-3.5 h-3.5 mr-1.5" /><SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="quality">Quality Score</SelectItem>
            <SelectItem value="cost">Lowest Cost</SelectItem>
            <SelectItem value="latency">Fastest</SelectItem>
            <SelectItem value="context">Context Size</SelectItem>
            <SelectItem value="name">Name</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Comparison Table */}
      <Card className="bg-[var(--qn-card)] border-[var(--qn-border)] overflow-hidden" data-testid="model-comparison-table">
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-[var(--qn-border)] bg-[var(--qn-surface)]/50">
                  <th className="text-left py-3 px-4 text-[var(--qn-foreground-muted)] font-medium text-xs">Model</th>
                  <th className="text-left py-3 px-4 text-[var(--qn-foreground-muted)] font-medium text-xs">Provider</th>
                  <th className="text-center py-3 px-4 text-[var(--qn-foreground-muted)] font-medium text-xs">Quality</th>
                  <th className="text-right py-3 px-4 text-[var(--qn-foreground-muted)] font-medium text-xs">Input $/1M</th>
                  <th className="text-right py-3 px-4 text-[var(--qn-foreground-muted)] font-medium text-xs">Output $/1M</th>
                  <th className="text-right py-3 px-4 text-[var(--qn-foreground-muted)] font-medium text-xs">Latency</th>
                  <th className="text-right py-3 px-4 text-[var(--qn-foreground-muted)] font-medium text-xs">Context</th>
                  <th className="text-left py-3 px-4 text-[var(--qn-foreground-muted)] font-medium text-xs">Tags</th>
                  <th className="py-3 px-4"></th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((model, idx) => (
                  <motion.tr
                    key={model.id}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: idx * 0.03 }}
                    className="border-b border-[var(--qn-border)]/50 hover:bg-[var(--qn-surface)]/30 cursor-pointer transition-colors"
                    onClick={() => setDrawerModel(model)}
                    data-testid={`model-row-${model.id}`}
                  >
                    <td className="py-3 px-4">
                      <div className="flex items-center gap-2">
                        {model.featured && <Star className="w-3 h-3 text-amber-500 fill-amber-500" />}
                        <span className="font-medium font-mono text-[var(--qn-foreground)]">{model.id}</span>
                      </div>
                    </td>
                    <td className="py-3 px-4">
                      <Badge variant="outline" className="text-[10px]" style={{ borderColor: PROVIDER_COLORS[model.provider] + "40", color: PROVIDER_COLORS[model.provider] }}>
                        {model.provider}
                      </Badge>
                    </td>
                    <td className="py-3 px-4 text-center"><QualityBar value={model.quality} /></td>
                    <td className="py-3 px-4 text-right font-mono text-xs text-[var(--qn-foreground)]">${model.inputCost.toFixed(2)}</td>
                    <td className="py-3 px-4 text-right font-mono text-xs text-[var(--qn-foreground)]">${model.outputCost.toFixed(2)}</td>
                    <td className="py-3 px-4 text-right font-mono text-xs text-[var(--qn-foreground)]">{model.latency}ms</td>
                    <td className="py-3 px-4 text-right font-mono text-xs text-[var(--qn-foreground)]">{(model.context / 1000).toFixed(0)}K</td>
                    <td className="py-3 px-4">
                      <div className="flex gap-1 flex-wrap">
                        {model.tags.slice(0, 2).map((t) => (
                          <Badge key={t} variant="secondary" className="text-[9px] bg-[var(--qn-surface)] border-[var(--qn-border)]">{t}</Badge>
                        ))}
                        {model.tags.length > 2 && <Badge variant="secondary" className="text-[9px]">+{model.tags.length - 2}</Badge>}
                      </div>
                    </td>
                    <td className="py-3 px-4"><ChevronRight className="w-4 h-4 text-[var(--qn-foreground-muted)]" /></td>
                  </motion.tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Detail Drawer */}
      <Sheet open={!!drawerModel} onOpenChange={(open) => !open && setDrawerModel(null)}>
        <SheetContent className="bg-[var(--qn-card)] border-l border-[var(--qn-border)] w-[420px] sm:w-[480px] overflow-y-auto" data-testid="model-detail-drawer">
          {drawerModel && (
            <div className="space-y-6">
              <SheetHeader>
                <div className="flex items-center gap-2">
                  {drawerModel.featured && <Star className="w-4 h-4 text-amber-500 fill-amber-500" />}
                  <SheetTitle className="font-outfit text-xl">{drawerModel.id}</SheetTitle>
                </div>
                <SheetDescription>
                  <Badge variant="outline" className="text-xs" style={{ borderColor: PROVIDER_COLORS[drawerModel.provider] + "40", color: PROVIDER_COLORS[drawerModel.provider] }}>
                    {drawerModel.provider}
                  </Badge>
                </SheetDescription>
              </SheetHeader>

              <p className="text-sm text-[var(--qn-foreground-muted)]">{drawerModel.description}</p>

              {/* Pricing */}
              <div>
                <div className="text-xs font-semibold text-[var(--qn-foreground-muted)] uppercase tracking-wider mb-3">Pricing (per 1M tokens)</div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="p-3 rounded-lg bg-[var(--qn-surface)] border border-[var(--qn-border)]">
                    <div className="text-[10px] text-[var(--qn-foreground-muted)]">Input</div>
                    <div className="text-lg font-bold font-mono text-[var(--qn-foreground)]">${drawerModel.inputCost.toFixed(2)}</div>
                  </div>
                  <div className="p-3 rounded-lg bg-[var(--qn-surface)] border border-[var(--qn-border)]">
                    <div className="text-[10px] text-[var(--qn-foreground-muted)]">Output</div>
                    <div className="text-lg font-bold font-mono text-[var(--qn-foreground)]">${drawerModel.outputCost.toFixed(2)}</div>
                  </div>
                </div>
              </div>

              {/* Performance */}
              <div>
                <div className="text-xs font-semibold text-[var(--qn-foreground-muted)] uppercase tracking-wider mb-3">Performance</div>
                <div className="space-y-2.5">
                  {[
                    { label: "Quality Score", value: drawerModel.quality, suffix: "/100", color: drawerModel.quality >= 90 ? "#10b981" : "#f59e0b" },
                    { label: "Median Latency", value: drawerModel.latency, suffix: "ms", color: drawerModel.latency < 200 ? "#10b981" : "#f59e0b" },
                    { label: "Context Window", value: (drawerModel.context / 1000).toFixed(0) + "K", color: "#8b5cf6" },
                  ].map((row) => (
                    <div key={row.label} className="flex items-center justify-between py-1.5 border-b border-[var(--qn-border)]/30">
                      <span className="text-xs text-[var(--qn-foreground-muted)]">{row.label}</span>
                      <span className="text-sm font-mono font-medium" style={{ color: row.color }}>{row.value}{row.suffix || ""}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Benchmarks */}
              <div>
                <div className="text-xs font-semibold text-[var(--qn-foreground-muted)] uppercase tracking-wider mb-3">Benchmarks</div>
                <div className="space-y-3">
                  {Object.entries(drawerModel.benchmarks).map(([key, val]) => (
                    <div key={key}>
                      <div className="flex justify-between text-xs mb-1">
                        <span className="text-[var(--qn-foreground-muted)] uppercase">{key}</span>
                        <span className="font-mono text-[var(--qn-foreground)]">{val}</span>
                      </div>
                      <div className="w-full h-2 rounded-full bg-[var(--qn-surface)] overflow-hidden">
                        <motion.div
                          className="h-full rounded-full"
                          initial={{ width: 0 }}
                          animate={{ width: `${val}%` }}
                          transition={{ duration: 0.6, ease: "easeOut" }}
                          style={{ background: `linear-gradient(90deg, var(--qn-primary), var(--qn-secondary))` }}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Usage Stats */}
              <div>
                <div className="text-xs font-semibold text-[var(--qn-foreground-muted)] uppercase tracking-wider mb-3">Usage on QNNEAL</div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="p-3 rounded-lg bg-[var(--qn-surface)] border border-[var(--qn-border)]">
                    <div className="text-[10px] text-[var(--qn-foreground-muted)]">Monthly Requests</div>
                    <div className="text-sm font-bold font-mono text-[var(--qn-foreground)]">{drawerModel.monthlyRequests}</div>
                  </div>
                  <div className="p-3 rounded-lg bg-[var(--qn-surface)] border border-[var(--qn-border)]">
                    <div className="text-[10px] text-[var(--qn-foreground-muted)]">Avg Tokens/Req</div>
                    <div className="text-sm font-bold font-mono text-[var(--qn-foreground)]">{drawerModel.avgTokens}</div>
                  </div>
                </div>
              </div>

              {/* Tags */}
              <div>
                <div className="text-xs font-semibold text-[var(--qn-foreground-muted)] uppercase tracking-wider mb-3">Capabilities</div>
                <div className="flex flex-wrap gap-2">
                  {drawerModel.tags.map((t) => (
                    <Badge key={t} variant="outline" className="text-xs border-[var(--qn-primary)]/20 text-[var(--qn-primary)]">{t}</Badge>
                  ))}
                </div>
              </div>
            </div>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}
